import { logger } from "@/logging";
import { BaseRenderer } from "../video/BaseRenderer";
import type { Three, WebGLRenderer, PerspectiveCamera, OrthographicCamera, Scene } from './types';
import { GRAPH_BG } from '@/ui/shared/utils';

export class ThreeRenderer extends BaseRenderer {

  three: Three;
  renderer: WebGLRenderer;
  camera: PerspectiveCamera | OrthographicCamera | null = null;
  scene: Scene | null = null;

  // Axes helper
  private axesScene: Scene | null = null;
  private axesCamera: PerspectiveCamera | null = null;
  private _showAxes: boolean = false;

  // Grid helper
  private gridHelper: any = null;
  private _showGrid: boolean = false;

  constructor(three: Three) {
    super();
    this.three = three;
  }

  set showAxes(value: boolean) {
    this._showAxes = value;
    if (value && !this.axesScene) {
      this.createAxesHelper();
    }
  }

  get showAxes(): boolean {
    return this._showAxes;
  }

  set showGrid(value: boolean) {
    this._showGrid = value;
    this.updateGrid();
  }

  get showGrid(): boolean {
    return this._showGrid;
  }

  private updateGrid(): void {
    if (!this.three || !this.scene) return;

    if (this._showGrid && !this.gridHelper) {
      // Create grid: 20 units, 20 divisions, grey colors
      this.gridHelper = new this.three.GridHelper(20, 20, 0x888888, 0x444444);
      this.scene.add(this.gridHelper);
    } else if (!this._showGrid && this.gridHelper) {
      this.scene.remove(this.gridHelper);
      this.gridHelper.dispose();
      this.gridHelper = null;
    }
  }

  private createAxesHelper(): void {
    if (!this.three) return;

    // Create a separate scene for axes
    this.axesScene = new this.three.Scene();

    // Create axes with arrows
    const axisLength = 1;
    const arrowHeadLength = 0.2;
    const arrowHeadWidth = 0.1;

    // X axis (red)
    const xDir = new this.three.Vector3(1, 0, 0);
    const xArrow = new this.three.ArrowHelper(xDir, new this.three.Vector3(0, 0, 0), axisLength, 0xff4444, arrowHeadLength, arrowHeadWidth);
    this.axesScene.add(xArrow);

    // Y axis (green) - UP
    const yDir = new this.three.Vector3(0, 1, 0);
    const yArrow = new this.three.ArrowHelper(yDir, new this.three.Vector3(0, 0, 0), axisLength, 0x44ff44, arrowHeadLength, arrowHeadWidth);
    this.axesScene.add(yArrow);

    // Z axis (blue)
    const zDir = new this.three.Vector3(0, 0, 1);
    const zArrow = new this.three.ArrowHelper(zDir, new this.three.Vector3(0, 0, 0), axisLength, 0x4444ff, arrowHeadLength, arrowHeadWidth);
    this.axesScene.add(zArrow);

    // Camera for axes (fixed distance, matches main camera rotation)
    this.axesCamera = new this.three.PerspectiveCamera(50, 1, 0.1, 10);
    this.axesCamera.position.set(0, 0, 3);
    this.axesCamera.lookAt(0, 0, 0);
  }

  protected initializeRenderer(canvas: HTMLCanvasElement): void {
    logger.trace('ThreeRenderer', 'initializeRenderer called');
    if (!this.three) {
      logger.error('ThreeRenderer', 'three not available');
      return;
    }

    try {
      this.renderer = new this.three.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true
      });
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = this.three.PCFSoftShadowMap;
      this.renderer.setSize(canvas.width, canvas.height);
      // Don't set pixel ratio - it causes viewport misalignment
      this.renderer.setPixelRatio(1);
      this.renderer.setClearColor(GRAPH_BG);
      logger.trace('ThreeRenderer', 'WebGLRenderer created successfully, canvas size:', canvas.width, 'x', canvas.height);
    } catch (err) {
      logger.error(`Failed to initialize Three.js renderer: ${err}`);
    }
  }

  protected _render() {
    if (!this.renderer || !this.scene || !this.camera) {
      return;
    }
    try {
      // Render main scene
      this.renderer.setViewport(0, 0, this.renderer.domElement.width, this.renderer.domElement.height);
      this.renderer.render(this.scene, this.camera);

      // Render axes helper in corner
      if (this._showAxes && this.axesScene && this.axesCamera) {
        // Match axes camera orientation to main camera
        // Copy the main camera's quaternion to the axes camera position (inverted to orbit around axes)
        const mainCamPos = this.camera.position.clone().normalize().multiplyScalar(3);
        this.axesCamera.position.copy(mainCamPos);
        this.axesCamera.up.copy(this.camera.up);
        this.axesCamera.lookAt(0, 0, 0);

        // Render in bottom-left corner
        const size = Math.min(80, this.renderer.domElement.width * 0.15);
        const margin = 10;
        this.renderer.setScissorTest(true);
        this.renderer.setScissor(margin, margin, size, size);
        this.renderer.setViewport(margin, margin, size, size);
        this.renderer.setClearColor(0x222222, 0.8);
        this.renderer.clear();
        this.renderer.render(this.axesScene, this.axesCamera);
        this.renderer.setScissorTest(false);

        // Reset viewport and clear color
        this.renderer.setViewport(0, 0, this.renderer.domElement.width, this.renderer.domElement.height);
        this.renderer.setClearColor(GRAPH_BG);
      }
    } catch (err) {
      logger.error(`Three.js render error: ${err}`);
    }
  }


  setSize(width: number, height: number): void {
    logger.trace('ThreeRenderer', 'setSize called:', width, 'x', height);
    const canvas = this.getCanvas();
    if (this.renderer && canvas) {
      canvas.width = width;
      canvas.height = height;
      this.renderer.setSize(width, height);

      // Update camera on resize
      if (this.camera) {
        if ('isPerspectiveCamera' in this.camera) {
          const cam = this.camera as PerspectiveCamera;
          cam.aspect = width / height;
          cam.updateProjectionMatrix();
        } else if ('isOrthographicCamera' in this.camera) {
          // Maintain ortho size while adjusting for aspect ratio
          const cam = this.camera as OrthographicCamera;
          const aspect = width / height;
          const halfHeight = (cam.top - cam.bottom) / 2;
          cam.left = -halfHeight * aspect;
          cam.right = halfHeight * aspect;
          cam.updateProjectionMatrix();
        }
      }
    }
  }
}